/**
 * Which strike the live terminal's CE/PE selector should hold after a chain
 * reload — the decision only, no DOM (static/live.html applies it).
 *
 * WHY THIS EXISTS. `loadChain(true)` re-fetches the option chain every 20s and
 * rebuilds both strike selectors from `chain.strikes`. That list is a WINDOW
 * around ATM, and it travels with spot. The old code kept the user's strike only
 * if it happened to still be inside the new window and otherwise fell back to the
 * ATM+offset default — silently.
 *
 * That is a truth violation, not a cosmetic one. The selector drives
 * `resolveContract`, which rebinds `contracts[side]` to a broker-verified
 * tradingsymbol; that symbol is what the Buy/Sell buttons order, what the card's
 * label shows, and what the LTP socket streams. So a strike drifting out of the
 * window silently re-pointed the whole card at a DIFFERENT contract, with no
 * signal. A strike is an explicit user choice: only the user may change it.
 *
 * The rule: a previous selection is ALWAYS kept, in or out of window. The
 * ATM+offset default applies only when there is no previous selection — first
 * load, or an explicit symbol/expiry change (`loadChain(false)`), where the user
 * has necessarily just chosen something new.
 */

export interface StrikeChoice {
  /** The option value to select — always a previous selection when there was one. */
  value: string;
  /**
   * True when `value` is NOT among the freshly returned strikes, so the caller
   * must re-offer it as an option before selecting it. The chain window is a
   * server-side view, not a constraint on what the user may hold.
   */
  offWindow: boolean;
}

/**
 * @param prev    the currently selected option value, or null/"" when the caller
 *                is deliberately resetting (`loadChain(false)`).
 * @param strikes the strikes in the freshly returned chain, in chain order.
 * @param atmStrike `chain.atm_strike`, used only for the no-previous case.
 * @param offset  index steps from ATM (settings.ceOffset / peOffset).
 * @returns the choice, or null to leave the selector at its natural default
 *          (no previous selection AND no ATM row to anchor to).
 */
export function chooseStrike(
  prev: string | null | undefined,
  strikes: readonly number[],
  atmStrike: number | null | undefined,
  offset: number,
): StrikeChoice | null {
  if (prev) {
    // STICKY. Note there is no `else` that drops the selection — that absence is
    // the fix. Out-of-window only changes how it is offered, never whether.
    return { value: prev, offWindow: !strikes.some((s) => String(s) === prev) };
  }
  const atmIdx = strikes.findIndex((s) => s === atmStrike);
  if (atmIdx < 0) return null;
  const idx = Math.min(Math.max(atmIdx + (offset || 0), 0), strikes.length - 1);
  return { value: String(strikes[idx]), offWindow: false };
}

/**
 * Where to splice an out-of-window strike back into the option list so the list
 * stays ascending — which keeps `stepStrike`'s selectedIndex±1 walking to the
 * genuinely adjacent strike.
 *
 * @returns the index of the first strike greater than `strike`, or -1 to append.
 */
export function insertionIndex(strikes: readonly number[], strike: number): number {
  return strikes.findIndex((s) => Number(s) > Number(strike));
}
